/**
 * Admin readouts that join Cloud SQL `User` rows (or rollup tables derived from them)
 * must match website-api customer integrity: non-test, non-internal, consent present.
 *
 * Funnel steps from admin Product (MR !36 Free funnel) use consented first-party
 * analytics events — consent is enforced at collect time, not via a User SQL join.
 *
 * Canonical SQL/Prisma predicate: website-api `customerUserWhere()` — see handbook !191.
 */

/** @typedef {'consented_analytics_funnel' | 'cloud_sql_customer_user' | 'stripe_joined_cloud_sql_user'} AdminMetricsIntegrityKind */

/** Free→Basic trial grant — user stays Free in billing until trial ends. */
export const BASIC_TRIAL_STATUS_ACTIVE = 'active';

export const WEBSITE_API_STRIPE_PAID_COUNTS = Object.freeze({
  repoPath: 'APIs/website-api-group/website-api',
  file: 'services/adminStripeCache.js',
  symbol: 'adminMrrSummary',
  joinSymbol: 'stripeSubscriptionJoinedCustomerUser',
});

/**
 * Cloud SQL User columns for customer metrics (website-api source of truth).
 * Keep in sync with website-api `customerUserWhere` / User model.
 */
export const CLOUD_SQL_USER_INTEGRITY = Object.freeze({
  isTest: false,
  isInternal: false,
  /** User row must have privacy/terms consent recorded (non-null timestamp). */
  consentField: 'consentGivenAt',
});

export const WEBSITE_API_CUSTOMER_USER_WHERE = Object.freeze({
  repoPath: 'APIs/website-api-group/website-api',
  file: 'services/adminMetrics/customerUserWhere.js',
  symbol: 'customerUserWhere',
});

export const WEBSITE_API_PRODUCT_ANALYTICS_COLLECT = Object.freeze({
  repoPath: 'APIs/website-api-group/website-api',
  file: 'services/productAnalyticsCollect.js',
  symbol: 'collectProductAnalytics',
});

/**
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {boolean}
 */
export function passesCloudSqlCustomerUserIntegrity(row) {
  if (!row || typeof row !== 'object') return false;
  if (row.isTest === true || row.isInternal === true) return false;
  const consent = row[CLOUD_SQL_USER_INTEGRITY.consentField];
  return consent != null && consent !== '';
}

/** @param {Record<string, unknown>|null|undefined} user */
export function isActiveBasicTrial(user) {
  return user?.basicTrialStatus === BASIC_TRIAL_STATUS_ACTIVE;
}

/**
 * Paid customer user for conversion readouts (not raw Stripe, not active Basic trial).
 * @param {Record<string, unknown>|null|undefined} user
 */
export function countsAsPaidCustomerUser(user) {
  if (!passesCloudSqlCustomerUserIntegrity(user)) return false;
  if (isActiveBasicTrial(user)) return false;
  return true;
}

/**
 * Stripe active subscription counts only when joined to a passing Cloud SQL user.
 * Never count raw Stripe subs (including internal/test company-domain metadata).
 *
 * @param {{ user?: Record<string, unknown>|null, stripeSubscriptionActive?: boolean }|null|undefined} row
 */
export function countsAsPaidStripeSubscription(row) {
  if (!row?.stripeSubscriptionActive) return false;
  if (!row.user) return false;
  return countsAsPaidCustomerUser(row.user);
}

/**
 * @param {ReadonlyArray<{ user?: Record<string, unknown>|null, stripeSubscriptionActive?: boolean }>} rows
 */
export function countPaidStripeSubscriptions(rows) {
  return (rows || []).filter((row) => countsAsPaidStripeSubscription(row)).length;
}

export const CONSENTED_FUNNEL_INTEGRITY_CAPTION =
  'Consented first-party analytics only (cookie/opt-in). May under-count vs server signups.';

export const CLOUD_SQL_CUSTOMER_INTEGRITY_CAPTION =
  'Counts customer User rows only: isTest and isInternal false, consent timestamp present (website-api customerUserWhere).';

export const SERVER_CONVERSIONS_INTEGRITY_CAPTION =
  'Server-side signup and paid-tier applies. Not cookie-gated; excludes test, internal, and users without consent.';

export const PAID_CONVERSION_INTEGRITY_CAPTION =
  'Paid = distinct customer users with subscription_started in range (excludes test, internal, no consent, and active Basic trial grants).';

export const STRIPE_PAID_SUBSCRIPTION_INTEGRITY_CAPTION =
  'Paying accounts = Stripe subs joined to Cloud SQL users (customerUserWhere). Never raw Stripe. Excludes test/internal and basicTrialStatus=active trial grants.';

/**
 * GraphQL operations the admin SPA uses for funnel / signup / paid-conversion readouts.
 * Filtering happens in website-api resolvers — this registry locks UI ↔ API expectations.
 *
 * @type {ReadonlyArray<{
 *   id: string,
 *   kind: AdminMetricsIntegrityKind,
 *   operationName: string,
 *   graphqlField: string,
 *   adminQueriesExport: string,
 *   consumers: ReadonlyArray<{ module: string, symbol: string }>,
 *   websiteApi: Readonly<{ repoPath: string, file: string, symbol: string }>,
 *   funnelStepIds?: ReadonlyArray<string>,
 * }>}
 */
export const ADMIN_FUNNEL_AND_CONVERSION_READOUTS = Object.freeze([
  {
    id: 'product_paid_funnel',
    kind: 'consented_analytics_funnel',
    operationName: 'AdminProductAnalytics',
    graphqlField: 'adminProductAnalytics.funnel',
    adminQueriesExport: 'ADMIN_PRODUCT_ANALYTICS',
    consumers: [
      { module: 'src/modules/product/ProductModule.js', symbol: 'PAID_FUNNEL_STEPS' },
      { module: 'src/modules/product/ProductModule.js', symbol: 'SignupFunnelTable' },
    ],
    websiteApi: WEBSITE_API_PRODUCT_ANALYTICS_COLLECT,
    funnelStepIds: [
      'pricing_page_viewed',
      'signup_plan_step_viewed',
      'checkout_initiated',
      'checkout_completed',
    ],
  },
  {
    id: 'product_free_funnel_mr36',
    kind: 'consented_analytics_funnel',
    operationName: 'AdminProductAnalytics',
    graphqlField: 'adminProductAnalytics.funnel',
    adminQueriesExport: 'ADMIN_PRODUCT_ANALYTICS',
    consumers: [
      { module: 'src/modules/product/ProductModule.js', symbol: 'FREE_FUNNEL_STEPS' },
      { module: 'src/modules/product/ProductModule.js', symbol: 'SignupFunnelTable' },
    ],
    websiteApi: WEBSITE_API_PRODUCT_ANALYTICS_COLLECT,
    funnelStepIds: ['signup_page_viewed', 'free_signup_completed'],
  },
  {
    id: 'product_server_conversions',
    kind: 'cloud_sql_customer_user',
    operationName: 'AdminProductPaths',
    graphqlField: 'adminProductAnalytics.serverConversions',
    adminQueriesExport: 'ADMIN_PRODUCT_PATHS',
    consumers: [
      {
        module: 'src/modules/product/ProductModule.js',
        symbol: 'serverConversions AnalyticsBars',
      },
    ],
    websiteApi: WEBSITE_API_CUSTOMER_USER_WHERE,
  },
  {
    id: 'product_activation_funnel',
    kind: 'cloud_sql_customer_user',
    operationName: 'AdminActivationFunnel',
    graphqlField: 'adminActivationFunnel',
    adminQueriesExport: 'ADMIN_ACTIVATION_FUNNEL',
    consumers: [{ module: 'src/modules/product/ProductModule.js', symbol: 'SignupActivation' }],
    websiteApi: WEBSITE_API_CUSTOMER_USER_WHERE,
  },
  {
    id: 'growth_paid_signups_by_source',
    kind: 'cloud_sql_customer_user',
    operationName: 'AdminPaidSignupsBySource',
    graphqlField: 'adminPaidSignupsBySource',
    adminQueriesExport: 'ADMIN_PAID_SIGNUPS_BY_SOURCE',
    consumers: [{ module: 'src/modules/growth/GrowthModule.js', symbol: 'PaidBySourceTable' }],
    websiteApi: WEBSITE_API_CUSTOMER_USER_WHERE,
  },
  {
    id: 'home_kpi_signups',
    kind: 'cloud_sql_customer_user',
    operationName: 'AdminKpiSummary',
    graphqlField: 'adminKpiSummary.signups',
    adminQueriesExport: 'ADMIN_KPI_SUMMARY',
    consumers: [{ module: 'src/modules/home/HomeModule.js', symbol: 'HomeModule' }],
    websiteApi: WEBSITE_API_CUSTOMER_USER_WHERE,
  },
  {
    id: 'home_signup_tier_mix',
    kind: 'cloud_sql_customer_user',
    operationName: 'AdminSignupsByTierInRange',
    graphqlField: 'adminSignupsByTierInRange',
    adminQueriesExport: 'ADMIN_SIGNUPS_BY_TIER_IN_RANGE',
    consumers: [{ module: 'src/modules/home/HomeModule.js', symbol: 'splitSignupTiers' }],
    websiteApi: WEBSITE_API_CUSTOMER_USER_WHERE,
  },
  {
    id: 'growth_signup_series',
    kind: 'cloud_sql_customer_user',
    operationName: 'AdminSignupSeries',
    graphqlField: 'adminSignupSeries',
    adminQueriesExport: 'ADMIN_SIGNUP_SERIES',
    consumers: [{ module: 'src/modules/growth/GrowthModule.js', symbol: 'GrowthModule' }],
    websiteApi: WEBSITE_API_CUSTOMER_USER_WHERE,
  },
  {
    id: 'growth_signups_by_tier_series',
    kind: 'cloud_sql_customer_user',
    operationName: 'AdminSignupsByTierSeries',
    graphqlField: 'adminSignupsByTierSeries',
    adminQueriesExport: 'ADMIN_SIGNUPS_BY_TIER_SERIES',
    consumers: [{ module: 'src/modules/growth/GrowthModule.js', symbol: 'rebucketSignupTierSeries' }],
    websiteApi: WEBSITE_API_CUSTOMER_USER_WHERE,
  },
  {
    id: 'revenue_mrr_summary',
    kind: 'stripe_joined_cloud_sql_user',
    operationName: 'AdminMrrSummary',
    graphqlField: 'adminMrrSummary',
    adminQueriesExport: 'ADMIN_MRR_SUMMARY',
    consumers: [
      { module: 'src/modules/revenue/RevenueModule.js', symbol: 'RevenueModule' },
      { module: 'src/modules/home/HomeModule.js', symbol: 'HomeModule' },
      { module: 'src/modules/economics/components/ActualsPanel.js', symbol: 'ActualsPanel' },
    ],
    websiteApi: WEBSITE_API_STRIPE_PAID_COUNTS,
  },
  {
    id: 'revenue_mrr_series',
    kind: 'stripe_joined_cloud_sql_user',
    operationName: 'AdminMrrSeries',
    graphqlField: 'adminMrrSeries',
    adminQueriesExport: 'ADMIN_MRR_SERIES',
    consumers: [
      { module: 'src/modules/revenue/RevenueModule.js', symbol: 'ADMIN_MRR_SERIES' },
      { module: 'src/modules/forecast/components/CalibrateFromActuals.js', symbol: 'CalibrateFromActuals' },
    ],
    websiteApi: WEBSITE_API_STRIPE_PAID_COUNTS,
  },
]);

/** @param {string} operationName */
export function readoutsForOperation(operationName) {
  return ADMIN_FUNNEL_AND_CONVERSION_READOUTS.filter((row) => row.operationName === operationName);
}

/** @param {AdminMetricsIntegrityKind} kind */
export function readoutsByKind(kind) {
  return ADMIN_FUNNEL_AND_CONVERSION_READOUTS.filter((row) => row.kind === kind);
}
